import { useEffect, useState } from "react";
import {
  Modal,
  TextInput,
  SelectBox,
  TagBox,
  TextAreaInput,
  Grid,
  Button,
  styled,
} from "@monolith-forensics/monolith-ui";
import { useForm } from "@mantine/form";
import { z } from "zod";
import validateAsync from "@/utils/ZodHelpers/validateAsync";
import ButtonMenu from "@/components/Forms/components/ButtonMenu";

import { GetTypes } from "../types";
import { AssetTypeSelectBox } from "@/Features/LabAssets";
import type { CustomFieldAPIBundle } from "../utils/customFieldAPIBundler";
import { useQuery } from "@tanstack/react-query";
import shortUUID from "short-uuid";
import ErrorComponent from "@/Monolith-UI/Error";
import CustomFieldsApi from "@/api/custom-fields";
import { CustomField, CUSTOM_FIELD_CATEGORIES } from "@/Features";

const FormContent = styled.div`
  margin: 20px 0px;
`;

const FullWidthColumn = styled.div`
  grid-column: span 2;
`;

type EditorType = "textBox" | "dateBox" | "dropDown" | "tagBox";

interface EditCustomFieldFormData {
  field_id: number;
  field_name: string;
  is_required: 0 | 1;
  editor_type: EditorType | "";
  options: string[];
  description: string;
  type_ids: number[];
  linked_id: number | undefined;
}

const validationSchema = z
  .object({
    field_name: z.string().min(1, { message: "Field name is required" }),
    is_required: z.number(),
    editor_type: z.string().min(1, { message: "Editor type is required" }),
    options: z.array(z.string()).optional(),
    description: z.string().optional(),
    type_ids: z.array(z.number()).optional(),
    linked_id: z.number().nullable().optional(),
  })
  .refine(
    (data) =>
      !["dropDown", "tagBox"].includes(data.editor_type) ||
      (data.options && data.options.length > 0),
    { message: "At least one option is required", path: ["options"] }
  );

interface EditCustomFieldProps {
  handleOpen: boolean;
  customFields: CustomField[];
  setCustomFields: React.Dispatch<React.SetStateAction<CustomField[] | null>>;
  handleUpdateField: CustomFieldAPIBundle["updateField"];
  getTypes: CustomFieldAPIBundle["getTypes"];
  category: string;
  title: string;
  editSelection: CustomField | null;
  handleClose: () => void;
}

const editorTypeOptions = [
  { label: "Text Box", value: "textBox" },
  { label: "Date", value: "dateBox" },
  { label: "Drop Down Menu", value: "dropDown" },
  { label: "Tag Box", value: "tagBox" },
];

const isRequiredOptions = [
  { label: "Yes", value: 1 },
  { label: "No", value: 0 },
];

const EditCustomField = ({
  handleOpen = false,
  setCustomFields,
  handleUpdateField,
  getTypes = null,
  category = "",
  title = "Edit Custom Field",
  editSelection,
  handleClose = () => {},
}: EditCustomFieldProps) => {
  const [showSelections, setShowSelections] = useState<boolean>(false);
  const [associatedTypes, setAssociatedTypes] = useState<GetTypes | []>([]);
  const [selectedEditorType, setSelectedEditorType] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<{ uuid: string; error: string }[]>([]);

  const form = useForm<EditCustomFieldFormData>({
    mode: "uncontrolled",
    initialValues: {
      field_id: 0,
      field_name: "",
      is_required: 0,
      editor_type: "",
      options: [],
      description: "",
      type_ids: [],
      linked_id: undefined,
    },
  });

  const { data: caseCustomFields } = useQuery({
    queryKey: ["custom-fields", "case"],
    queryFn: () =>
      CustomFieldsApi.getCustomFields({
        category: CUSTOM_FIELD_CATEGORIES.CASE,
      }),
    enabled: category === CUSTOM_FIELD_CATEGORIES.INQUIRY,
  });

  useEffect(() => {
    if (getTypes) {
      getTypes().then((result) => {
        setAssociatedTypes(result);
      });
    }
  }, []);

  useEffect(() => {
    if (handleOpen && editSelection) {
      const editorType = editSelection.editor_type || "";
      const options = Array.isArray(editSelection.options)
        ? editSelection.options
        : editSelection.options
        ? JSON.parse(editSelection.options as unknown as string)
        : [];

      const values = {
        field_id: editSelection.field_id,
        field_name: editSelection.field_name || "",
        is_required: editSelection.is_required ?? 0,
        editor_type: editorType,
        options,
        description: editSelection.description || "",
        type_ids: editSelection.type_ids || [],
        linked_id: editSelection.linked_id ?? undefined,
      };

      form.setInitialValues(values);
      form.setValues(values);

      setSelectedEditorType(editorType);
      setShowSelections(editorType === "tagBox" || editorType === "dropDown");
    }
  }, [handleOpen, editSelection]);

  const handleSubmit = async () => {
    if (!editSelection) return;

    const formData = form.getValues();

    const validationResult = await validateAsync(validationSchema)(formData);
    if (validationResult?.hasErrors) {
      form.setErrors(validationResult.errors);
      return;
    }

    setSubmitting(true);
    try {
      const editorType = formData.editor_type as EditorType;
      const isRequired = formData.is_required as 0 | 1;

      await handleUpdateField({
        field_id: formData.field_id,
        field_name: formData.field_name,
        is_required: isRequired,
        editor_type: editorType,
        options: formData.options?.length ? formData.options : null,
        description: formData.description || null,
        type_ids: formData.type_ids?.length ? formData.type_ids : null,
        linked_id: formData.linked_id,
      });
      setCustomFields((prev) => {
        if (!prev) {
          return null;
        }
        return prev.map((field: CustomField) => {
          if (field.field_id === editSelection.field_id) {
            return {
              ...field,
              field_name: formData.field_name,
              is_required: isRequired,
              editor_type: editorType,
              options: formData.options?.length ? formData.options : null,
              description: formData.description,
              type_ids: formData.type_ids?.length ? formData.type_ids : null,
              linked_id: formData.linked_id,
            };
          }
          return field;
        });
      });
      setShowSelections(false);
      handleClose();
    } catch (e) {
      setErrors((prevErrors) => [
        ...prevErrors,
        {
          uuid: shortUUID.generate(),
          error: `${e}`,
        },
      ]);
    } finally {
      setSubmitting(false);
    }
  };

  const onCancel = () => {
    setShowSelections(false);
    form.reset();
    handleClose();
  };

  const handleErrorClose = (uuid: string) => {
    const updatedErrors = errors.filter((error) => error.uuid !== uuid);
    setErrors(updatedErrors);
  };

  const handleEditorTypeChange = (value: string) => {
    setSelectedEditorType(value);
    if (["tagBox", "dropDown"].includes(value)) {
      setShowSelections(true);
    } else {
      setShowSelections(false);
    }
  };

  const linkedFieldOptions = [
    { label: "None", value: null },
    ...(caseCustomFields
      ?.filter((field: CustomField) => {
        if (
          selectedEditorType === "dropDown" ||
          selectedEditorType === "textBox"
        ) {
          return (
            field.editor_type === "dropDown" || field.editor_type === "textBox"
          );
        }
        return field.editor_type === selectedEditorType;
      })
      .map((field: CustomField) => ({
        label: field.field_name,
        value: field.field_id,
      })) || []),
  ];

  const associatedTypeOptions = (associatedTypes as any[]).map((type) => ({
    label: type.evi_type,
    value: type.evi_type_id,
  }));

  return (
    <Modal open={handleOpen} onClose={onCancel} style={{ width: 550 }}>
      <Modal.Title>{title}</Modal.Title>
      {errors.map((error) => (
        <ErrorComponent
          key={error.uuid}
          error={error.error}
          onClose={() => handleErrorClose(error.uuid)}
        />
      ))}
      <FormContent>
        <Grid col={2}>
          <TextInput
            label="Field Name"
            description="The display name for this custom field"
            placeholder="Name this field..."
            required
            key={form.key("field_name")}
            {...form.getInputProps("field_name")}
          />
          <SelectBox
            label="Is Required"
            description="Whether this field is required"
            required
            data={isRequiredOptions}
            placeholder="Select..."
            key={form.key("is_required")}
            {...form.getInputProps("is_required")}
          />
          <SelectBox
            label="Editor Type"
            description="This cannot be edited after field creation "
            required
            disabled
            data={editorTypeOptions}
            placeholder="Select..."
            key={form.key("editor_type")}
            {...form.getInputProps("editor_type")}
            onChange={(value: string) => {
              form.getInputProps("editor_type").onChange(value);
              handleEditorTypeChange(value);
            }}
          />
          {category === CUSTOM_FIELD_CATEGORIES.INQUIRY ? (
            <SelectBox
              label="Case Field Mapping"
              description="Map this field to an existing case field"
              data={linkedFieldOptions}
              placeholder="Select..."
              disabled={!selectedEditorType}
              key={form.key("linked_id")}
              {...form.getInputProps("linked_id")}
            />
          ) : null}
          {category === CUSTOM_FIELD_CATEGORIES.ASSET ? (
            <AssetTypeSelectBox
              key={form.key("type_ids")}
              {...form.getInputProps("type_ids")}
              disabled
            />
          ) : getTypes ? (
            <TagBox
              label="Associated Types"
              description="Limit this field to specific types"
              data={associatedTypeOptions}
              placeholder="Select types..."
              searchable
              key={form.key("type_ids")}
              {...form.getInputProps("type_ids")}
            />
          ) : null}
          {showSelections && (
            <FullWidthColumn>
              <TagBox
                label="Selection Options"
                description="Add items that will appear as selectable options"
                required
                data={[]}
                placeholder="Type to add Items..."
                allowCustomValue
                key={form.key("options")}
                {...form.getInputProps("options")}
              />
            </FullWidthColumn>
          )}
          <TextAreaInput
            label="Description"
            description="Optional description displayed with this field"
            colSpan={2}
            key={form.key("description")}
            {...form.getInputProps("description")}
          />
        </Grid>
      </FormContent>
      <ButtonMenu>
        <Button
          size="xs"
          variant="subtle"
          onClick={onCancel}
          disabled={submitting}
        >
          Cancel
        </Button>
        <Button
          size="xs"
          color="primary"
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting}
          loading={submitting}
        >
          Save
        </Button>
      </ButtonMenu>
    </Modal>
  );
};

export default EditCustomField;
